import { Children } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import Breadcrumbs from "../../components/shared/Breadcrumbs.jsx";
import ShareLinks from "../../components/shared/ShareLinks.jsx";
import ResponsiveImage from "../../components/shared/ResponsiveImage.jsx";
import ImageCredit from "../../components/shared/ImageCredit.jsx";
import FaqAccordion from "../../components/shared/FaqAccordion.jsx";
import EventGallery from "../../components/events/EventGallery.jsx";
import EventFactBar from "../../components/events/EventFactBar.jsx";
import EventCharacteristics from "../../components/events/EventCharacteristics.jsx";
import EventSchedule from "../../components/events/EventSchedule.jsx";
import EventGoodToKnow from "../../components/events/EventGoodToKnow.jsx";
import EventVenueCard from "../../components/events/EventVenueCard.jsx";
import { sectionHeadingStyle } from "../../components/events/eventSectionStyles.js";
import { events, getEventBySlug, findByLangAndId, excerpt, formatDate, slugify } from "../../utils/content.js";
import { buildEventJsonLd, buildBreadcrumbJsonLd, buildFaqJsonLd } from "../../utils/jsonld.js";
import { eventCategoryLabel } from "../../utils/eventCategories.js";
import { ageRangeLabel, timeRangeLabel } from "../../utils/eventFormat.js";
import { CONTACT_SUBJECT_HASH } from "../Kontakt.jsx";
import imageManifest from "../../data/imageManifest.json";
import NotFound from "../NotFound.jsx";

const KONTAKT_PATH = { de: "kontakt", en: "contact" };

const badgeStyle = {
  fontSize: "12px",
  color: "var(--color-cream-soft)",
  border: "1px solid rgba(254,250,239,0.25)",
  padding: "5px 12px",
  borderRadius: "999px",
};

/**
 * Line number of the body's first real paragraph — the one the design
 * sets as a serif lead. Computed from the source rather than counted
 * during render: a render-time counter would double up under React's
 * double-invoked render in development and land on a different paragraph
 * than the prerender snapshot did.
 */
function firstParagraphLine(markdown) {
  const lines = markdown.split("\n");
  for (const [index, line] of lines.entries()) {
    const text = line.trim();
    if (!text) continue;
    // Headings, lists, quotes, tables and fences are not the lead.
    if (/^(#|[-*+>|]|\d+\.|```|!\[)/.test(text)) continue;
    return index + 1;
  }
  return null;
}

/** The body's own `##` headings get the same gold rule as the structured
 * sections around them, so the page reads as one document rather than as
 * markdown dropped between components. */
function BodyH2({ children }) {
  const text = Children.toArray(children)
    .map((child) => (typeof child === "string" ? child : ""))
    .join("");
  return (
    <h2 id={slugify(text)} style={{ ...sectionHeadingStyle, marginTop: "8px" }}>
      {children}
    </h2>
  );
}

/** The opening paragraph carries the invitation; everything after it is
 * detail. Serif and a size up, as in the design. */
function BodyParagraph({ node, children, leadLine }) {
  const isLead = leadLine != null && node?.position?.start?.line === leadLine;
  return isLead ? (
    <p style={{ fontFamily: "var(--font-serif)", fontSize: "19px", lineHeight: 1.7, color: "rgba(254,250,239,0.78)", maxWidth: "66ch", margin: 0 }}>
      {children}
    </p>
  ) : (
    <p style={{ margin: 0, maxWidth: "66ch" }}>{children}</p>
  );
}

export default function EventDetail({ lang }) {
  const { slug } = useParams();
  const { t } = useTranslation();
  const event = getEventBySlug(lang, slug);

  if (!event) return <NotFound lang={lang} />;

  const { frontmatter: f, content } = event;
  const heroImageLoaded = Boolean(imageManifest[f.image]);
  const otherLang = lang === "de" ? "en" : "de";
  // Sibling found by `id`, not by assuming the same slug — events happen
  // to share slugs across languages today, but nothing requires it.
  const sibling = findByLangAndId(events, otherLang, f.id);
  const altPath = sibling ? `/events/${sibling.frontmatter.slug}` : `/events/${slug}`;
  const hasTranslation = Boolean(sibling);
  const breadcrumbItems = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.events"), path: "/events" },
    { name: f.title, path: `/events/${slug}` },
  ];

  const dateBadge =
    formatDate(f.date, lang) +
    (f.endDate && f.endDate !== f.date ? ` – ${formatDate(f.endDate, lang)}` : "") +
    (timeRangeLabel(f, lang) ? ` · ${timeRangeLabel(f, lang)}` : "");
  const ageLabel = ageRangeLabel(f, lang);
  const leadLine = firstParagraphLine(content);
  const soldOut = f.spotsLeft === 0;
  // A past event's seat count is a historical fact nobody needs; only a
  // live event's availability is worth a line.
  const showAvailability = f.status !== "past" && f.spotsLeft != null;

  return (
    <>
      <SEOHead
        lang={lang}
        path={`/events/${slug}`}
        altPath={altPath}
        hasTranslation={hasTranslation}
        title={f.metaTitle || f.title}
        category={f.metaTitle ? undefined : eventCategoryLabel(f.category, lang)}
        description={f.metaDescription || excerpt(content)}
        image={f.image}
        imageAlt={f.imageAlt}
        publishedTime={f.publishedAt}
        modifiedTime={f.updatedAt || f.publishedAt}
      />
      <JsonLd data={buildEventJsonLd(f, lang, { url: `https://talesandlegends.org/${lang}/events/${slug}` })} />
      <JsonLd data={buildBreadcrumbJsonLd(breadcrumbItems, lang)} />
      {f.embeddedFaq?.length > 0 && <JsonLd data={buildFaqJsonLd(f.embeddedFaq)} />}

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "48px", paddingTop: "28px", paddingBottom: "96px" }}>
          <Breadcrumbs items={breadcrumbItems} lang={lang} />

          <div style={{ position: "relative", borderRadius: "var(--radius-card-lg)", overflow: "hidden", minHeight: "440px", display: "flex" }}>
            <ResponsiveImage
              src={f.image}
              alt={f.imageAlt || f.title}
              priority
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            />
            {heroImageLoaded && (
              <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(11,9,8,0.45), rgba(11,9,8,0.8))" }} />
            )}
            <div style={{ position: "relative", display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: "18px", padding: "48px 44px", width: "100%" }}>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <span className="tl-badge" style={{ background: "var(--color-gold)" }}>
                  {dateBadge}
                </span>
                <span style={badgeStyle}>{eventCategoryLabel(f.category, lang)}</span>
                {ageLabel && <span style={badgeStyle}>{ageLabel}</span>}
                {f.externalOrga && (
                  <span style={badgeStyle}>
                    {f.orgaName ? `${t("events.externalOrgaBadgeWith")} ${f.orgaName}` : t("events.externalOrgaBadge")}
                  </span>
                )}
                {f.tags?.map((tag) => (
                  <span key={tag} style={badgeStyle}>
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "50px", lineHeight: 1.06, color: "var(--color-cream)", maxWidth: "22ch", margin: 0 }}>
                {f.title}
              </h1>
              {f.subtitle && (
                <p style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "19px", color: "var(--color-gold)", margin: 0 }}>
                  {f.subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Under the hero rather than on it: the hero carries the H1 and
            * badges, and a credit line over a photo is unreadable against
            * a busy image. */}
          <ImageCredit credit={f.imageCredit} style={{ textAlign: "right", marginTop: "-32px" }} />

          <EventFactBar event={f} lang={lang} />

          <div className="tl-grid-2" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 380px", gap: "56px", alignItems: "start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "56px" }}>
              <article
                className="tl-event-body"
                style={{ fontSize: "17px", lineHeight: 1.8, color: "var(--color-cream-soft)", display: "flex", flexDirection: "column", gap: "20px" }}
              >
                <ReactMarkdown
                  components={{
                    h2: BodyH2,
                    p: (props) => <BodyParagraph {...props} leadLine={leadLine} />,
                  }}
                >
                  {content}
                </ReactMarkdown>
              </article>

              <EventCharacteristics characteristics={f.characteristics} />
              <EventSchedule items={f.schedule} lang={lang} />
              <EventGoodToKnow items={f.goodToKnow} />

              {f.embeddedFaq?.length > 0 && (
                <section style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <h2 style={sectionHeadingStyle}>{t("events.faqTitle")}</h2>
                  <FaqAccordion items={f.embeddedFaq} />
                </section>
              )}

              {/* The visible counterpart to article:published_time /
               * article:modified_time. The design draft dropped it; a page
               * that reports an update date only to crawlers is claiming
               * something it won't show a reader. */}
              {f.publishedAt && (
                <p style={{ fontSize: "13px", color: "var(--color-cream-faint)", margin: 0 }}>
                  {t("common.publishedOn", { date: formatDate(f.publishedAt, lang) }) +
                    (f.updatedAt && f.updatedAt !== f.publishedAt
                      ? ` · ${t("common.updatedOn", { date: formatDate(f.updatedAt, lang) })}`
                      : "")}
                </p>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "20px", position: "sticky", top: "96px" }}>
              <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "28px", borderTop: "3px solid var(--color-gold)" }}>
                <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-ink)" }}>{t("events.ticketsTitle")}</span>

                {f.tickets?.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {f.tickets.map((ticket) => (
                      <div
                        key={ticket.type || ticket.label}
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          justifyContent: "space-between",
                          gap: "16px",
                          padding: "14px 0",
                          borderBottom: "1px solid rgba(10,9,7,0.12)",
                        }}
                      >
                        <span style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                          <span style={{ fontSize: "15px", color: "var(--color-ink)" }}>{ticket.label}</span>
                          {ticket.note && <span style={{ fontSize: "12px", color: "rgba(10,9,7,0.68)" }}>{ticket.note}</span>}
                        </span>
                        <span style={{ fontFamily: "var(--font-serif)", fontSize: "19px", color: "var(--color-ink)" }}>{`${ticket.price} €`}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", background: "rgba(10,9,7,0.05)", borderRadius: "10px", padding: "16px 18px" }}>
                      <span style={{ fontSize: "15px", color: "var(--color-ink)" }}>{t("events.priceFrom")}</span>
                      <span style={{ fontFamily: "var(--font-serif)", fontSize: "18px", color: "var(--color-ink)" }}>{`${f.price} €`}</span>
                    </div>
                    {f.priceReduced != null && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", background: "rgba(206,149,50,0.2)", borderRadius: "10px", padding: "16px 18px" }}>
                        <span style={{ fontSize: "15px", color: "var(--color-ink)" }}>{t("events.reduced")}</span>
                        <span style={{ fontFamily: "var(--font-serif)", fontSize: "18px", color: "var(--color-ink)" }}>{`${f.priceReduced} €`}</span>
                      </div>
                    )}
                  </>
                )}

                {f.registrationOpen && f.registrationUrl && (
                  <a href={f.registrationUrl} target="_blank" rel="noreferrer" className="tl-pill tl-pill-dark" style={{ width: "100%", padding: "15px 0", textAlign: "center" }}>
                    {t("events.registrationCta")}
                  </a>
                )}

                {showAvailability && (
                  <span style={{ fontSize: "12px", color: "rgba(10,9,7,0.68)", textAlign: "center" }}>
                    {(soldOut
                      ? t("events.soldOut")
                      : t("events.spotsLeft", { left: f.spotsLeft, capacity: f.capacity ?? "" })) +
                      (f.refundNote ? ` · ${f.refundNote}` : "")}
                  </span>
                )}

                {/* Deep-links into the contact form with "Freiplatz" already
                 * selected — a hash, not a query parameter, so it creates no
                 * crawlable duplicate of /kontakt (§ T-06). */}
                {f.freeTicketRequest !== false && f.status !== "past" && (
                  <Link
                    to={`/${lang}/${KONTAKT_PATH[lang]}${CONTACT_SUBJECT_HASH.freiplatz}`}
                    className="tl-pill tl-pill-ghost-dark"
                    style={{ width: "100%", padding: "13px 0", textAlign: "center" }}
                  >
                    {t("events.freeTicketCta")}
                  </Link>
                )}
              </div>

              <EventVenueCard event={f} />

              <div style={{ display: "flex", flexDirection: "column", gap: "10px", background: "var(--color-bg-raised)", borderRadius: "var(--radius-card-lg)", padding: "24px" }}>
                <ShareLinks shortUrl={f.shortUrl} title={f.title} />
              </div>
            </div>
          </div>

          <EventGallery images={f.gallery} title={f.galleryTitle} />

          <Link to={`/${lang}/events`} style={{ fontSize: "13px", color: "var(--color-cream-soft)" }}>
            {`← ${t("nav.events")}`}
          </Link>
        </div>
      </div>
    </>
  );
}
